// src/pages/AboutPage.jsx
import React from "react";
import { Box, Typography, Container, Grid, Button, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Subtle Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(#1e3a8a 0.5px, transparent 0.5px),
            radial-gradient(#1e3a8a 0.5px, #ffffff 0.5px)
          `,
          backgroundSize: "40px 40px",
          backgroundPosition: "0 0, 20px 20px",
          opacity: 0.03,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          position: "relative",
          zIndex: 10,
        }}
      >
        {/* Logo */}
        <Box
          onClick={() => navigate("/")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "rgba(30, 58, 138, 0.05)",
            borderRadius: 999,
            px: 2.5,
            py: 0.75,
            border: "1px solid rgba(30, 58, 138, 0.1)",
            cursor: "pointer",
            "&:hover": { bgcolor: "rgba(30, 58, 138, 0.1)" },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#1e3a8a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "20px" }}>
              EXI
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              color: "#1e3a8a",
              fontWeight: "bold",
              fontSize: "22px",
            }}
          >
            exivis
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
          <Button
            onClick={() => navigate("/")}
            sx={{
              color: "#1e3a8a",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "16px",
              "&:hover": { bgcolor: "rgba(30, 58, 138, 0.05)" },
            }}
          >
            Home
          </Button>
          <Button
            sx={{
              color: "#2563eb",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "16px",
              borderBottom: "2px solid #2563eb",
              borderRadius: 0,
              "&:hover": { bgcolor: "rgba(30, 58, 138, 0.05)" },
            }}
          >
            About
          </Button>
          <Button
            onClick={() => navigate("/contact")}
            sx={{
              color: "#1e3a8a",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "16px",
              "&:hover": { bgcolor: "rgba(30, 58, 138, 0.05)" },
            }}
          >
            Contact
          </Button>
          <Button
            onClick={() => navigate("/login")}
            variant="contained"
            sx={{
              bgcolor: "#1e3a8a",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "16px",
              px: 3,
              borderRadius: 999,
              "&:hover": {
                bgcolor: "#1e40af",
              },
            }}
          >
            Login
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{
          flex: 1,
          position: "relative",
          zIndex: 10,
          py: 6,
        }}
      >
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                fontWeight: 800,
                color: "#1e3a8a",
                mb: 3,
                lineHeight: 1.2,
              }}
            >
              About{" "}
              <Box component="span" sx={{ color: "#2563eb" }}>
                exivis
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#475569",
                fontSize: "18px",
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.8,
                fontWeight: 500,
              }}
            >
              exivis is your intelligent assistant that helps you think, decide, write, plan, and act—faster. 
              Built for everyone, not just tech experts. No complex jargon, no intimidating categories. 
              Just practical AI assistance that makes your daily life easier and more productive.
            </Typography>
          </Box>
        </motion.div>

        {/* Mission Section */}
        <Grid container spacing={4} sx={{ mb: 8, justifyContent: "center" }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ height: "100%" }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 4,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: "#1e3a8a",
                    mb: 2,
                    fontWeight: 800,
                  }}
                >
                  Our Mission
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#475569",
                    fontSize: "16px",
                    lineHeight: 1.8,
                    flex: 1,
                    fontWeight: 500,
                  }}
                >
                  Our mission is simple: make AI assistance accessible, practical, and genuinely useful for everyone. 
                  We believe AI shouldn't require a technical degree to use. That's why exivis focuses on real-world 
                  features you'll use every day—from rewriting emails to making decisions, turning thoughts into plans, 
                  and getting clear answers without the fluff.
                </Typography>
              </Paper>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              style={{ height: "100%" }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 4,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: "#1e3a8a",
                    mb: 2,
                    fontWeight: 800,
                  }}
                >
                  What We Do
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#475569",
                    fontSize: "16px",
                    lineHeight: 1.8,
                    flex: 1,
                    fontWeight: 500,
                  }}
                >
                  exivis provides seven core features designed for daily use: Smart Rewrite & Improve for any text, 
                  Clear Answers without long essays, Decision Helper for life choices, Thought-to-Plan converter, 
                  Everyday Writing Assistant, Personal Memory for preferences, and One-Tap Actions for quick results. 
                  Everything you need, nothing you don't.
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>

        {/* EXI Fusion Stack Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                color: "#0f172a",
                mb: 3,
                textAlign: "center",
                fontWeight: 800,
              }}
            >
              EXI Fusion Stack
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#64748b",
                fontSize: "16px",
                maxWidth: "720px",
                mx: "auto",
                mb: 4,
                textAlign: "center",
                lineHeight: 1.8,
                fontWeight: 500,
              }}
            >
              Behind the simple chat interface, EXI Orchestrator quietly routes every request
              through the right internal engine—answers, code, projects, media, languages,
              or real‑time context—and then combines everything into one clean, reliable reply.
              You never have to think about "which model" to use.
            </Typography>

            <Grid container spacing={3} sx={{ justifyContent: "center" }}>
              {[
                {
                  title: "Core Answer Engine",
                  description:
                    "Handles everyday questions with direct, no‑fluff answers and optional explanations.",
                },
                {
                  title: "Code & Bug Solver Engine",
                  description:
                    "Understands repositories, errors, and architecture to debug, refactor, and explain code across languages.",
                },
                {
                  title: "Project Download Engine",
                  description:
                    "Turns a natural language brief into a structured project with files, folders, and setup notes.",
                },
                {
                  title: "Media Engine",
                  description:
                    "Works with images, video, and audio for understanding, explanation, and creative content.",
                },
                {
                  title: "Multi‑Language Engine",
                  description:
                    "Supports multiple human and programming languages for translation, learning, and localization.",
                },
                {
                  title: "Real‑Time Intelligence Engine",
                  description:
                    "Designed to plug into live data sources for up‑to‑date, trend‑aware reasoning where needed.",
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} lg={4} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.3s, box-shadow 0.3s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.05)",
                      }
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#1e3a8a",
                        mb: 1.5,
                        fontWeight: 700,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#475569",
                        fontSize: "14px",
                        lineHeight: 1.7,
                        flex: 1,
                        fontWeight: 500,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* Why exivis Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                color: "#0f172a",
                mb: 4,
                textAlign: "center",
                fontWeight: 800,
              }}
            >
              Why Choose exivis?
            </Typography>
            <Grid container spacing={3} sx={{ justifyContent: "center" }}>
              {[
                {
                  title: "No Learning Curve",
                  description: "Start using exivis immediately. No tutorials, no complex setup. Just paste your text, ask your question, or describe your goal—and get results.",
                },
                {
                  title: "Real-World Focus",
                  description: "We built features based on what people actually need: rewriting emails, making decisions, planning tasks, and getting clear answers—not theoretical concepts.",
                },
                {
                  title: "Clear, Not Confusing",
                  description: "Unlike other AI tools that give long essays, exivis gives you direct answers first, then optional explanations. Bullets, not paragraphs.",
                },
                {
                  title: "Affordable Platform",
                  description: "exivis is designed to be accessible for everyone. Start for free and enjoy the power of multiple AI engines in one simple interface.",
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} lg={3} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#1e3a8a",
                        mb: 2,
                        fontWeight: 700,
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#475569",
                        fontSize: "14px",
                        lineHeight: 1.6,
                        flex: 1,
                        fontWeight: 500,
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Box
            sx={{
              textAlign: "center",
              p: { xs: 4, md: 8 },
              bgcolor: "#1e3a8a",
              borderRadius: 6,
              color: "white",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* CTA Background Decoration */}
            <Box 
              sx={{ 
                position: "absolute", 
                top: -100, 
                right: -100, 
                width: 300, 
                height: 300, 
                borderRadius: "50%", 
                bgcolor: "rgba(255, 255, 255, 0.05)",
                zIndex: 0
              }} 
            />
            
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  color: "white",
                  mb: 2,
                  fontWeight: 800,
                }}
              >
                Ready to Get Started?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: "18px",
                  mb: 4,
                  fontWeight: 500,
                  maxWidth: "600px",
                  mx: "auto",
                }}
              >
                Start using exivis today—free to get started, with flexible plans that grow with your needs. 
                No credit card required for the free tier.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/signup")}
                sx={{
                  bgcolor: "#ffffff",
                  color: "#1e3a8a",
                  px: 6,
                  py: 1.5,
                  fontSize: "18px",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 999,
                  "&:hover": {
                    bgcolor: "#f1f5f9",
                  },
                }}
              >
                Start Free Today
              </Button>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}

